import React, { Component } from 'react';
import ShopifyInstructions from './ShopifyInstructions'
import ShopifyReinstallation from './ShopifyReinstallation'

import apiClient from '../../services/apiClient'

import API_ENDPOINTS from '../../constants/apiEndpoints'

import isValidMapboxClientKey from '../../utils/mapbox';

export default class ShopifyInstallation extends Component {
  state = {
    isLoading: false,
    showingSectionOption: false,
    showingTemplateOption: false,
    showingOverlayOption: false,
    showingIframePageEmbed: false,
  };

  toggleOption = (optionName) => {
    return () => {
      const newState = {};
      newState[optionName] = !this.state[optionName];
      this.setState(newState);
    }
  }

  get renderCategorySpecificInstructions() {
    const { showingIframePageEmbed } = this.state;

    if (showingIframePageEmbed) {
      return (
        <p>
          To restrict a map to a specific category, you can do so by adding a url param <code>?category=[CATEGORY NAME]</code> when embedding your iframe. <a href="https://closeby.zendesk.com/hc/en-us/articles/360023316974" target="_blank">Learn more</a>
        </p>
      )
    } else {
      return (
        <p>
          To restrict a map to a specific category, you can do so by adding the <code>category: '[CATEGORY NAME]'</code> property to the options in your embed code. <a href="https://closeby.zendesk.com/hc/en-us/articles/360023316974" target="_blank">Learn more</a>
        </p>
      )
    }
  }

  get renderSectionOption() {
    const { imgInstallThemeSection } = this.props;
    const { EMBED_HOST, WEB_HOST } = window.PAGEDATA;
    const { showingIframePageEmbed } = this.state;

    return (
      <div className="page-setup-option-content">
        <img src={imgInstallThemeSection} className="page-setup-option-content-img mbl" />
        <div className="mbl">
          <p>
            To use Closeby in your Shopify theme, just select "Store Locator - Closeby" in your theme editor to add it as a new section. Select one of the section style options and your map will appear in your theme automatically.
          </p>
          <p>
            <b>That's it! You have your custom map integrated.</b>
          </p>
        </div>

        <ShopifyReinstallation />
        <hr className="mtxl mbl" />

        <div>
          <p>
            If you'd like a more custom setup, you may need to use your embed code and add it to a Custom HTML block instead:
          </p>
        </div>
        <div className="code-snippet-wrapper">
          <div className="code-snippet-label">
            Paste this code to embed your map into your website.

            <div className="code-snippet-label-switch" onClick={this.toggleOption('showingIframePageEmbed')}>
              {showingIframePageEmbed ? 'Use Embed Code' : 'Use iFrame Code'}
            </div>
          </div>
          {showingIframePageEmbed ? (
            <pre className="code-snippet"><code>&lt;iframe src=&quot;{WEB_HOST}/embed/{this.props.mapKey}&quot; width=&quot;100%&quot; height=&quot;600&quot; style=&quot;height:600px;border:none&quot; allow=&quot;geolocation&quot;&gt;&lt;/iframe&gt;</code></pre>
          ) : (
            <pre className="code-snippet"><code>&lt;div class=&quot;closeby-embed-{this.props.mapKey}&quot;&gt;&lt;/div&gt;<br />
              &lt;script src=&quot;{EMBED_HOST}/v1.js?closeby.mapKey={this.props.mapKey}&embed=true&height=700&quot; async defer&gt;&lt;/script&gt;</code>
            </pre>
          )}
        </div>
        <div className="mtl">
          <p>
            If you are manually installing your map on a Shopify store, view <a href="https://closeby.zendesk.com/hc/en-us/articles/360023478093" target="_blank">this article</a> for more information.
          </p>
          {this.renderCategorySpecificInstructions}
        </div>
      </div>
    )
  }

  get renderTemplateOption() {
    const { imgInstallPageSection, shopifyMetafieldsGenerated } = this.props

    return (
      <div className="page-setup-option-content">
        <img src={imgInstallPageSection} className="page-setup-option-content-img mbl" />

        {shopifyMetafieldsGenerated ? this.renderPageEmbedWithShopifyTemplates : this.renderPageEmbedWithoutShopifyTemplates}
      </div>
    )
  }

  get renderOverlayOption() {
    return (
      <div className="page-setup-option-content">
        <ShopifyInstructions />
        {this.renderJavascriptInstructions}
        {this.renderManualOpening}
      </div>
    )
  }

  get renderInstallationOptions () {
    const { shopifyMetafieldsGenerated } = this.props
    const { showingSectionOption, showingTemplateOption, showingOverlayOption } = this.state

    return (
      <div className="mtxl mbxl">
        <h3>
          Installation Overview
        </h3>
        <div>
          There are 3 approaches you can take for installing your map:
        </div>
        <div className="mtl">
          <div className="page-setup-options">
            <div className="page-setup-option">
              <div className="page-setup-option-toggle" onClick={this.toggleOption('showingSectionOption')}>
                <i className={`page-setup-option-icon ion-ios-arrow-down ${showingSectionOption ? 'open' : ''}`} />
                <h3>
                  Theme Section
                </h3>
                <div className="page-setup-option-subheader">
                  Use the Shopify theme editor to embed your map within your storefront.
                </div>
              </div>
              {showingSectionOption && this.renderSectionOption}
            </div>
            <div className="page-setup-option">
              <div className="page-setup-option-toggle" onClick={this.toggleOption('showingTemplateOption')}>
                <i className={`page-setup-option-icon ion-ios-arrow-down ${showingTemplateOption ? 'open' : ''}`} />
                <h3>
                  Single Page Template
                </h3>
                <div className="page-setup-option-subheader">
                  Use auto-generated page templates to create a stand-alone Shopify page for your locations.
                </div>
              </div>
              {showingTemplateOption && this.renderTemplateOption}
            </div>
            <div className="page-setup-option">
              <div className="page-setup-option-toggle" onClick={this.toggleOption('showingOverlayOption')}>
                <i className="page-setup-option-icon ion-ios-arrow-down" />
                <i className={`page-setup-option-icon ion-ios-arrow-down ${showingOverlayOption ? 'open' : ''}`} />
                <h3>
                  Global Overlay
                </h3>
                <div className="page-setup-option-subheader">
                  Add your map to your website to be opened as a modal from any page with custom links.
                </div>
              </div>
              {showingOverlayOption && this.renderOverlayOption}
            </div>
          </div>
        </div>

        {shopifyMetafieldsGenerated ? '' : (
          <div className="mtl text-center">
            To learn more, check out the <a href="https://closeby.zendesk.com/hc/en-us/articles/360006990953-Installing-your-map-on-your-website-" target="_blank">FAQ</a> here
          </div>
        )}
      </div>
    )
  }

  get renderJavascriptInstructions () {
    const { EMBED_HOST } =  window.PAGEDATA
    const { enableLauncher } = this.props

    return (
      <div className="mbxl" id="overlay-install">
        <h4>
          Overlay Manual Installation
        </h4>
        <div>
          <p>
            If you choose to manaully install the overlay instead of the using the automatic Shopify integration above, follow the steps below.
          </p>
          <p>
            Copy and paste the JavaScript code below to every page where you want your map to be available to visitors. Add it before the closing <code>&lt;/head&gt;</code> tag at the top of each page. For instructions on installing the widget on a Squarespace, you can follow <a href="https://closeby.zendesk.com/hc/en-us/articles/360006916254" target="_blank">these detailed instructions</a>.
          </p>
        </div>
        <div className="code-snippet-wrapper">
          <div className="code-snippet-label">
            Add this code:
          </div>
            <pre className="code-snippet"><code>&lt;script&gt;<br/>
              &nbsp;&nbsp;window.closeby = {'{'}<br/>
                &nbsp;&nbsp;&nbsp;&nbsp;mapKey: &quot;{this.props.mapKey}&quot;<br/>
              &nbsp;&nbsp;{'}'};<br/>
              &lt;/script&gt;<br/>
              &lt;script src=&quot;{EMBED_HOST}/v1.js&quot; async defer&gt;&lt;/script&gt;</code>
            </pre>
        </div>
        <div className="mtl">
          { enableLauncher ?
            'This will add the launcher in the bottom right corner of your website. If you do not want the default launcher and you would rather open the overlay yourself edit your theme and uncheck the "Enable Launcher" option.' :
            'The default launcher is disabled, use HTML code (see below) to open the overlay. If you want to re-enable the default launcher edit your theme and check the "Enable Launcher" option.'
          }
        </div>
      </div>
    )
  }

  get renderManualOpening () {
    const { enableLauncher } = this.props

    return (
      <div className="mbxl" id="manual-open-overlay">
        <h4>
          Manually Opening Overlay
        </h4>
        <div>
          { enableLauncher ?
            '' :
            <p className="text-success">
              Note: This step is required because you "Enable Launcher" is disabled for your map. If you want to skip this and use the default launcher instead edit your theme and check the box for "Enable Launcher".
            </p>
          }
          <p>
            Once the javascript code has been added, most users use the built-in toggle to open their map. Alternatively, you can manually trigger the map with your own custom HTML link.
          </p>
          <p>
            Add a link with a URL as <code>javascript:closeby-open</code> or <code>#closeby-open</code> to trigger your widget manually, as if it were an external website link. This can be useful if you have your own style or buttons that you want to use open the map.
          </p>
          <p>
            So instead of a normal link like <code>http://example.com</code>, simply add <code>javascript:closeby-open</code> as the URL and it will open the widget.
          </p>
        </div>
        <div className="code-snippet-wrapper">
          <div className="code-snippet-label">
            Example HTML link:
          </div>
          <pre className="code-snippet"><code>&lt;a href=&quot;javascript:closeby-open&quot;&gt;<br/>
            &nbsp;&nbsp;View Locations<br/>
          &lt;/a&gt;</code></pre>
        </div>
      </div>
    )
  }

  get renderPageEmbedWithoutShopifyTemplates() {
    return (
      <div className="mtl">
        <ShopifyReinstallation />
      </div>
    )
  }

  get renderPageEmbedWithShopifyTemplates() {
    const { showingIframePageEmbed } = this.state;
    const { EMBED_HOST, WEB_HOST } =  window.PAGEDATA

    return (
      <div>
        <div className="mbl">
          <p>
            If you wish to use your map embedded within the content of a Shopify page, all you need to do is select it as the page template.
          </p>
          <p>
            Just select one of the automatically generated templates (default, centered or full-width) when creating a page and you will have your custom map integrated. That's it!
          </p>
        </div>

        <ShopifyReinstallation />
        <hr className="mtxl mbl" />

        <div>
          <p>
            If you'd like set up a more customized setup with your iframe, follow the steps below.
          </p>
        </div>
        <div className="code-snippet-wrapper">
          <div className="code-snippet-label">
            Paste this code to embed your map into your website.

            <div className="code-snippet-label-switch" onClick={this.toggleOption('showingIframePageEmbed')}>
              {showingIframePageEmbed ? 'Use Embed Code' : 'Use iFrame Code'}
            </div>
          </div>
          {showingIframePageEmbed ? (
            <pre className="code-snippet"><code>&lt;iframe src=&quot;{WEB_HOST}/embed/{this.props.mapKey}&quot; width=&quot;100%&quot; height=&quot;600&quot; style=&quot;height:600px;border:none&quot; allow=&quot;geolocation&quot;&gt;&lt;/iframe&gt;</code></pre>
          ) : (
            <pre className="code-snippet"><code>&lt;div class=&quot;closeby-embed-{this.props.mapKey}&quot;&gt;&lt;/div&gt;<br />
              &lt;script src=&quot;{EMBED_HOST}/v1.js?closeby.mapKey={this.props.mapKey}&embed=true&height=700&quot; async defer&gt;&lt;/script&gt;</code>
            </pre>
          )}
        </div>
        <div className="mtl">
          <p>
            If you are manually installing your map on a Shopify store, view <a href="https://closeby.zendesk.com/hc/en-us/articles/360023478093" target="_blank">this article</a> for more information.
          </p>
          {this.renderCategorySpecificInstructions}
        </div>
      </div>
    )
  }

  render() {
    // Note: Can escape js embed code using https://www.freeformatter.com/html-escape.html
    const { enableLauncher, mapInstalled, mapbox_client_key: mapboxClientKey, valueKey } = this.props;
    const mapboxClientKeyValid = (mapboxClientKey && isValidMapboxClientKey(mapboxClientKey) && !valueKey) || (valueKey && isValidMapboxClientKey(valueKey));
 
    return mapboxClientKeyValid ? (
      <>
        <div className="page-setup-header">
          By following these simple instructions, you can easily display your map on your Shopify website in just a few minutes.
        </div>

        {this.renderInstallationOptions}
      </>
    ) : (
      <div className="page-setup-header">
        Provide a Mapbox Access Token above to proceed with installation instructions.
      </div>
    )
  };
};
